<template>
  <div
    v-if="currentList.length > 0"
    :id="id"
    class="table-container"
  >
    <table class="table">
      <thead v-if="hasHeader">
        <tr>
          <th
            v-for="(col, idx) in cols"
            :key="idx"
            :data-identifier="col.name"
            :style="{ maxWidth: col.maxWidth }"
            @click="isSortable(col) && sortBy(col)"
          >
            {{ col.label }}
            <IconArrow
              v-if="isSortable(col) && sortColumn === col.name"
              :direction="isSortingCrescent ? 'down' : 'up'"
              size="xs"
            />
            <IconUnfoldMore
              v-else-if="isSortable(col)"
              size="xs"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, rIndex) in currentList"
          :key="rIndex"
          :class="{ 'child-row': row.isChild }"
        >
          <td
            v-for="(col, cIndex) in cols"
            :key="cIndex"
            :data-identifier="col.name"
            :style="{ maxWidth: col.maxWidth }"
            :title="col.title ? row[col.title] : ''"
          >
            <slot
              class="table__row"
              :name="col.name"
              :row="row"
            >
              {{
                col.type === 'date' ? formatDate(row[col.name]) : row[col.name]
              }}
              <IconLock
                v-if="col.name === 'email' && row.blocked"
                title="Usuário bloqueado"
                class="lock-icon"
                size="xs"
              />
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
    <div
      v-if="hasPagination && list.length > 5"
      class="table__pagination"
    >
      <div class="table__pagination__items-per-page">
        <span> Itens por página: </span>
        <v-select
          v-model="itemsPerPage"
          :clearable="false"
          :searchable="false"
          :options="itemsPerPageOptions"
        />
      </div>
      <div class="table__pagination__counts">
        <div class="table__pagination__counts_items">
          {{ currentFirstItem + '-' + currentLastItem }} de {{ list.length }}
          {{ list.length > 1 ? 'itens' : 'item' }}
        </div>
        <div class="table__pagination__counts_pages">
          {{ totalPages }} {{ totalPages === 1 ? 'pagina' : 'páginas' }}
        </div>
      </div>
      <div class="table__pagination__nav">
        <button
          :disabled="currentPage === 1"
          @click="currentPage -= 1"
        >
          <IconArrow direction="left" />
        </button>
        <v-select
          v-model="currentPage"
          :clearable="false"
          :searchable="false"
          :options="pagesOptions"
        />
        <button
          :disabled="currentPage === totalPages"
          @click="currentPage += 1"
        >
          <IconArrow direction="right" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import getFormattedDate from '@/helpers/getFormattedDate';
import IconArrow from '@/components/icons/IconArrow.vue';
import IconUnfoldMore from '@/components/icons/IconUnfoldMore.vue';
import vSelect from 'vue-select';
import IconLock from '@/components/icons/IconLock.vue';

export default {
  name: 'CustomTable',
  components: {
    'v-select': vSelect,
    IconArrow,
    IconUnfoldMore,
    IconLock,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    list: {
      type: Array,
      required: true,
    },
    cols: {
      type: Array,
      required: true,
    },
    hasPagination: {
      type: Boolean,
      default: false,
    },
    hasHeader: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      itemsPerPageOptions: [5, 10, 15, 20, 25, 30, 40, 50],
      itemsPerPage: 25,
      currentPage: 1,
      sortColumn: null,
      isSortingCrescent: true,
    };
  },
  computed: {
    currentList() {
      if (this.hasPagination) {
        return this.flattenList(this.currentPageList);
      }
      return this.flattenList(this.orderedList);
    },
    orderedList() {
      if (this.list.length === 0 || !this.sortColumn) {
        return this.favoritesUp(this.list);
      }

      const orderedList = [...this.list];
      const colType = typeof orderedList[0][this.sortColumn];

      if (colType === 'string') {
        orderedList.sort((itemA, itemB) => {
          return this.isSortingCrescent
            ? itemA[this.sortColumn].localeCompare(itemB[this.sortColumn])
            : itemB[this.sortColumn].localeCompare(itemA[this.sortColumn]);
        });
      }

      if (['number', 'boolean'].includes(colType)) {
        orderedList.sort((itemA, itemB) => {
          return this.isSortingCrescent
            ? itemA[this.sortColumn] - itemB[this.sortColumn]
            : itemB[this.sortColumn] - itemA[this.sortColumn];
        });
      }

      return this.favoritesUp(orderedList);
    },
    totalPages() {
      return Math.ceil(this.list.length / this.itemsPerPage);
    },
    pagesOptions() {
      return Array.from({ length: this.totalPages }, (item, idx) => idx + 1);
    },
    currentFirstItem() {
      return this.itemsPerPage * (this.currentPage - 1) + 1;
    },
    currentLastItem() {
      return Math.min(this.itemsPerPage * this.currentPage, this.list.length);
    },
    currentPageList() {
      return this.orderedList.slice(
        this.currentFirstItem - 1,
        this.itemsPerPage * this.currentPage,
      );
    },
  },
  watch: {
    itemsPerPage(value) {
      if (value >= this.list.length) {
        this.currentPage = 1;
      }
      this.updateLocalStorageTableSettings();
    },
    currentList(list) {
      if (!list.length && this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },
  },
  mounted() {
    const tableSettings = localStorage.getItem(`table-settings-${this.id}`);

    if (tableSettings) {
      Object.assign(this, JSON.parse(tableSettings));
    }

    this.$on('search', () => {
      this.currentPage = 1;
    });
  },
  methods: {
    formatDate: getFormattedDate,
    isSortable(col) {
      return col.isSortable !== false;
    },
    flattenList(list) {
      const flatList = [];
      list.forEach((item) => {
        flatList.push(item);

        if (item.children) {
          flatList.push(
            ...item.children.map((child) => ({ ...child, isChild: true })),
          );
        }
      });
      return flatList;
    },
    sortBy(col) {
      this.currentPage = 1;

      if (this.sortColumn !== col.name) {
        this.isSortingCrescent = true;
      } else {
        this.isSortingCrescent = !this.isSortingCrescent;
      }

      this.sortColumn = col.name;
      this.updateLocalStorageTableSettings();
    },
    updateLocalStorageTableSettings() {
      const tableSettings = {
        sortColumn: this.sortColumn,
        isSortingCrescent: this.isSortingCrescent,
        itemsPerPage: this.itemsPerPage,
      };

      localStorage.setItem(
        `table-settings-${this.id}`,
        JSON.stringify(tableSettings),
      );
    },
    favoritesUp(orderedList) {
      return orderedList.sort((itemA, itemB) => {
        if (!!itemA.isFavorite === !!itemB.isFavorite) {
          return 0;
        }

        return itemB.isFavorite ? 1 : -1;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.table-container {
  position: relative;
  padding-bottom: 55px;
  min-height: 355px;

  table {
    width: 100%;
    margin-bottom: 0;

    tr + tr {
      border-top: 1px solid rgba($lb-light-gray, 0.5);
    }

    tr:last-child {
      border-bottom: 1px solid rgba($lb-light-gray, 0.5);
    }

    tr {
      min-height: 48px;
      width: 100%;
      display: flex;
      align-items: center;

      &.child-row {
        td:first-child > div {
          margin-left: 28px;
        }
      }

      &:hover {
        background-color: #f9f9f9;
      }
    }

    th > .icon {
      margin-left: 12px;
      flex-shrink: 0;
    }

    th,
    td {
      padding: 0 12px;
      display: flex;
      height: 100%;
      width: 100%;
      border: none;
      position: relative;
      align-items: center;
    }
  }

  .table {
    height: 100%;
    position: relative;
    border-spacing: 0 0 55px;

    &__pagination {
      height: 55px;
      position: absolute;
      bottom: 0;
      width: 100%;
      display: flex;
      justify-content: space-between;
      flex-shrink: 0;
      border: 1px solid $lb-light-gray;

      > div + div {
        border-left: 1px solid $lb-light-gray;
      }

      .v-select {
        width: 82px;

        ::v-deep {
          .vs__dropdown-toggle {
            border-bottom-color: #3c3c3c42;
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;

            .vs__selected-options {
              flex-direction: row-reverse;
            }
          }

          .vs__dropdown-menu {
            top: auto;
            bottom: calc(100% - 2px);
            border-radius: 4px 4px 0 0;
            border-top-style: solid;
            border-bottom-style: none;
            box-shadow: 0 -3px 6px rgb(0 0 0 / 15%);
            text-align: center;
            min-width: unset;
          }
        }
      }

      &__items-per-page {
        padding: 10px 20px;
        display: flex;
        align-items: center;
        white-space: nowrap;

        .v-select {
          margin-left: 10px;
        }
      }

      &__counts {
        padding: 10px 20px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      &__nav {
        display: flex;
        align-items: center;
        flex-shrink: 0;

        button {
          padding: 10px 20px;
        }
      }
    }
  }

  .lock-icon {
    margin-left: 4px;
    color: $danger;
  }
}
</style>
